<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 cursor-pointer hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
    @click="$emit('view', finding)">
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <span class="px-1.5 py-0.5 rounded text-xs font-medium" :class="platformClasses">
            <i :class="platformIcon" class="mr-1" />{{ finding.platform }}
          </span>
          <span class="px-1.5 py-0.5 rounded text-xs font-medium" :class="intentMeta(finding.intent).classes">
            {{ intentMeta(finding.intent).label }}
          </span>
          <span class="text-xs text-gray-400" v-tooltip="'AI relevance score (0–100)'">
            <i class="far fa-signal mr-0.5" />{{ finding.relevance_score }}
          </span>
          <span v-if="finding.conversation_posted_at" class="text-xs text-gray-400">
            {{ finding.conversation_posted_at }}
          </span>
          <span v-if="finding.topic" class="text-xs text-gray-400 truncate">
            · {{ finding.topic }}
          </span>
        </div>
        <a
          :href="finding.url" target="_blank" rel="noopener"
          class="font-medium text-primary-600 dark:text-primary-400 hover:underline break-words"
          @click.stop>
          {{ finding.title || finding.url }}
          <i class="far fa-external-link-alt text-xs ml-1" />
        </a>
        <p v-if="finding.snippet" class="text-sm text-gray-600 dark:text-gray-300 mt-1">
          "{{ finding.snippet }}"
        </p>
        <p v-if="finding.why_relevant" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          <i class="far fa-lightbulb mr-1" />{{ finding.why_relevant }}
        </p>
      </div>
      <div class="flex flex-col gap-1 shrink-0">
        <button
          v-if="finding.status !== 'reviewed'"
          class="btn btn-secondary btn-sm"
          v-tooltip="'Mark as reviewed'"
          @click.stop="$emit('set-status', finding, 'reviewed')">
          <i class="far fa-check mr-1" />Reviewed
        </button>
        <button
          v-if="finding.status !== 'dismissed'"
          class="btn btn-secondary btn-sm text-gray-500"
          v-tooltip="'Not relevant — hide from the New feed'"
          @click.stop="$emit('set-status', finding, 'dismissed')">
          <i class="far fa-times mr-1" />Dismiss
        </button>
        <button
          v-if="finding.status !== 'new'"
          class="btn btn-secondary btn-sm text-gray-500"
          @click.stop="$emit('set-status', finding, 'new')">
          Restore
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import { intentMeta } from './intents'

const props = defineProps({
  finding: { type: Object, required: true }
})

defineEmits(['set-status', 'view'])

const platformStyles = {
  reddit: { classes: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300', icon: 'fab fa-reddit-alien' },
  facebook: { classes: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300', icon: 'fab fa-facebook' },
  nextdoor: { classes: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300', icon: 'far fa-home' },
  other: { classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300', icon: 'far fa-comment' }
}

const platformClasses = computed(() => (platformStyles[props.finding.platform] || platformStyles.other).classes)
const platformIcon = computed(() => (platformStyles[props.finding.platform] || platformStyles.other).icon)
</script>
